import React, { useState } from 'react';
import { EEI_VALUE_EXEMPTION_NOTE, showEeiValueExemptionNote } from '../../utils/eeiValueNote';

interface AuditType {
  isExpands?: boolean;
  saveBtn?: any;
  selectedStep?: any;
  handleRowClick?: any;
  workflowData?: any;
  isSelected?: boolean;
  embedded?: boolean;
}

// A single label/value row, matching the other phase panels' layout exactly
// (classify-res-comp-sec-col > classify-res-comp-sec > left icon + label, then
// the value in an h2, with an optional secondary sub-line beneath it).
const DetailRow: React.FC<{
  label: string;
  value?: string | null;
  subValue?: string | null;
}> = ({ label, value, subValue }) => (
  <div className='classify-res-comp-sec-col'>
    <div className='classify-res-comp-sec'>
      <div className='classify-res-comp-sec-left'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
        >
          <path
            d='M9.33333 1.51314V4.26667C9.33333 4.6400 9.33333 4.82670 9.40599 4.96933C9.4699 5.09477 9.57189 5.19675 9.69733 5.26067C9.83994 5.33333 10.0266 5.33333 10.4 5.33333H13.1535M9.33333 11.3333H5.33333M10.6667 8.66667H5.33333M13.3333 6.65898V11.4667C13.3333 12.5868 13.3333 13.1468 13.1153 13.5746C12.9236 13.951 12.6176 14.2569 12.2413 14.4487C11.8135 14.6667 11.2534 14.6667 10.1333 14.6667H5.86667C4.74656 14.6667 4.18651 14.6667 3.75869 14.4487C3.38236 14.2569 3.0764 13.951 2.88465 13.5746C2.66667 13.1468 2.66667 12.5868 2.66667 11.4667V4.53333C2.66667 3.41323 2.66667 2.85317 2.88465 2.42535C3.0764 2.04903 3.38236 1.74307 3.75869 1.55132C4.18651 1.33333 4.74656 1.33333 5.86667 1.33333H8.00768C8.49574 1.33333 8.73977 1.33333 8.96942 1.38847C9.17301 1.43735 9.36764 1.51802 9.54614 1.62761C9.74749 1.75124 9.92004 1.92379 10.2651 2.26889L12.3978 4.40156C12.7429 4.74666 12.9155 4.91922 13.0391 5.12057C13.1487 5.29907 13.2293 5.4937 13.2782 5.69729C13.3333 5.92694 13.3333 6.17097 13.3333 6.65898Z'
            stroke='var(--text-classify)'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </div>
      <div className='classify-res-comp-sec-txt'>{label}</div>
    </div>
    <div>
      <h2
        style={{
          color: 'var(--Text-Main, var(--text-primary))',
          fontSize: '14px',
          fontWeight: '400',
          paddingLeft: '28px',
        }}
      >
        {value || 'N/A'}
      </h2>
      {subValue ? (
        <p
          style={{
            color: 'var(--text-secondary)',
            fontSize: '13px',
            fontWeight: '400',
            paddingLeft: '28px',
            margin: '4px 0 0',
            lineHeight: 1.4,
          }}
        >
          {subValue}
        </p>
      ) : null}
    </div>
  </div>
);

// Small section header inside the expanded panel (Product / Transaction / …).
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div className='classify-res-comp-sec' style={{ paddingTop: '4px' }}>
    <div
      className='classify-res-comp-sec-txt'
      style={{ fontWeight: 600, paddingLeft: '4px' }}
    >
      {title}
    </div>
  </div>
);

const Divider: React.FC = () => (
  <div className='export-divider' style={{ display: 'flex' }} />
);

const ProductProfileResults: React.FC<AuditType> = ({
  isExpands,
  handleRowClick,
  workflowData,
  isSelected,
  embedded = false,
}) => {
  const [isExpand, setIsExpand] = useState(isExpands || false);

  const profile = workflowData?.profile;
  const eei = workflowData?.eei;
  const scheduleB = workflowData?.schedule_b;

  const filingDetermination: string | null = eei?.filing_determination || null;
  // Gate "complete" on the phase's real terminal artifact — the EEI filing
  // determination — NOT the mere existence of the `profile` object (which the server
  // ships, non-null, from the first chunk). Mirrors the sibling cards (schedule_b gates
  // on schedule_b_code, country on normalized_country_name). Without this the green
  // check rendered immediately, before intake even finished (showed complete while
  // still running). While the determination is absent the card stays "in progress".
  const hasResult = Boolean(filingDetermination);

  // Collapsed summary: "Intangible export — Not Required — Excluded". Degrades
  // gracefully on old tickets that only carry the EEI determination.
  const collapsedSummary =
    [profile?.tangible_display, filingDetermination].filter(Boolean).join(' — ') ||
    'Awaiting information...';

  const providedCode = profile?.provided_code ?? scheduleB?.provided_code;
  const normalizedCode = profile?.normalized_code ?? scheduleB?.normalized_code;
  const codeResolution = profile?.code_resolution ?? scheduleB?.code_resolution;
  const hasCodeInfo = Boolean(providedCode || normalizedCode || codeResolution);

  const notes: string[] = Array.isArray(eei?.notes) ? eei.notes : [];

  const handleCardClick = () => {
    if (hasResult) {
      setIsExpand((prev) => !prev);
      return;
    }
    handleRowClick?.();
  };
  const handleExpandClick = (event: React.MouseEvent<SVGSVGElement>) => {
    event.stopPropagation();
    setIsExpand((prev) => !prev);
  };

  return (
    <>
      <div
        className={`classify-res-comp ${
          isSelected ? 'selected ' : ''
        }${embedded ? 'standard-workflow-product-profile-mirror' : ''}`.trim()}
      >
        {!embedded && (
          <div
            className='classify-res-comp-header'
            onClick={handleCardClick}
            style={{ cursor: 'pointer' }}
          >
          <div className='classify-res-comp-header-left-icon'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
            >
              <path
                d='M9.33333 1.51314V4.26667C9.33333 4.6400 9.33333 4.82670 9.40599 4.96933C9.4699 5.09477 9.57189 5.19675 9.69733 5.26067C9.83994 5.33333 10.0266 5.33333 10.4 5.33333H13.1535M9.33333 11.3333H5.33333M10.6667 8.66667H5.33333M13.3333 6.65898V11.4667C13.3333 12.5868 13.3333 13.1468 13.1153 13.5746C12.9236 13.951 12.6176 14.2569 12.2413 14.4487C11.8135 14.6667 11.2534 14.6667 10.1333 14.6667H5.86667C4.74656 14.6667 4.18651 14.6667 3.75869 14.4487C3.38236 14.2569 3.0764 13.951 2.88465 13.5746C2.66667 13.1468 2.66667 12.5868 2.66667 11.4667V4.53333C2.66667 3.41323 2.66667 2.85317 2.88465 2.42535C3.0764 2.04903 3.38236 1.74307 3.75869 1.55132C4.18651 1.33333 4.74656 1.33333 5.86667 1.33333H8.00768C8.49574 1.33333 8.73977 1.33333 8.96942 1.38847C9.17301 1.43735 9.36764 1.51802 9.54614 1.62761C9.74749 1.75124 9.92004 1.92379 10.2651 2.26889L12.3978 4.40156C12.7429 4.74666 12.9155 4.91922 13.0391 5.12057C13.1487 5.29907 13.2293 5.4937 13.2782 5.69729C13.3333 5.92694 13.3333 6.17097 13.3333 6.65898Z'
                stroke='var(--text-classify)'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <div className='classify-res-comp-header-text'>
            Product Profile & EEI Check
          </div>
          <div
            className='classify-res-comp-header-left-icon'
            style={{ paddingTop: '2px' }}
          >
            {hasResult ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <path
                  d='M13.3327 4L5.99935 11.3333L2.66602 8'
                  stroke='#17B26A'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <g clipPath='url(#clip0_profile_clock)'>
                  <path
                    d='M8.00016 3.99992V7.99992L10.6668 9.33325M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z'
                    stroke='#F79009'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_profile_clock'>
                    <rect width='16' height='16' fill='white' />
                  </clipPath>
                </defs>
              </svg>
            )}
          </div>
          <div
            className='classify-res-comp-header-left-icon'
            style={{ paddingTop: '2px' }}
          >
            {!isExpand ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                onClick={handleExpandClick}
              >
                <path
                  d='M10 12L6 8L10 4'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                onClick={handleExpandClick}
              >
                <path
                  d='M4 6L8 10L12 6'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            )}
          </div>
          </div>
        )}

        {!isExpand ? (
          <p
            className='classification-status'
            onClick={handleCardClick}
            style={{ cursor: 'pointer' }}
          >
            {collapsedSummary}
          </p>
        ) : (
          <>
            {profile ? (
              <>
                <SectionHeader title='Product' />
                <DetailRow label='Product Name' value={profile?.product_name} />
                <Divider />
                <DetailRow
                  label='Description'
                  value={profile?.product_description}
                />
                <Divider />
                <DetailRow label='End Use' value={profile?.end_use} />
                <Divider />

                <SectionHeader title='Transaction' />
                <DetailRow
                  label='Export Type'
                  value={profile?.export_type_display}
                />
                <Divider />
                <DetailRow
                  label='Tangibility'
                  value={profile?.tangible_display}
                />
                <Divider />
                <DetailRow
                  label='Destination Country'
                  value={profile?.destination_country}
                />
                <Divider />
                <DetailRow
                  label='Specially Designed'
                  value={profile?.specially_designed_answer}
                />
                <Divider />

                {hasCodeInfo ? (
                  <>
                    <SectionHeader title='Code' />
                    <DetailRow label='Provided Code' value={providedCode} />
                    <Divider />
                    <DetailRow label='Normalized Code' value={normalizedCode} />
                    <Divider />
                    <DetailRow label='Resolution' value={codeResolution} />
                    <Divider />
                  </>
                ) : null}
              </>
            ) : null}

            <SectionHeader title='EEI / AES Filing Determination' />
            <DetailRow
              label='Filing Determination'
              value={filingDetermination}
              subValue={eei?.determination_status}
            />
            <Divider />

            <DetailRow label='Basis' value={eei?.basis} />
            <Divider />

            <DetailRow label='Regulatory Citation' value={eei?.citation} />
            <Divider />

            <DetailRow label='Transaction Type' value={eei?.transaction_type} />
            <Divider />

            <DetailRow
              label='Destination Category'
              value={eei?.destination_class_display}
            />
            <Divider />

            <DetailRow
              label='Value Exemption (§30.37(a))'
              value={eei?.value_exemption}
              subValue={showEeiValueExemptionNote(eei) ? EEI_VALUE_EXEMPTION_NOTE : undefined}
            />
            <Divider />

            <DetailRow
              label='Does a license requirement force filing? (§30.2(a)(1)(iv))'
              value={eei?.license_override}
            />

            {eei?.aes_exemption_legend ? (
              <>
                <Divider />
                <DetailRow
                  label='AES Exemption Legend'
                  value={eei?.aes_exemption_legend}
                  subValue='Annotate this citation on your commercial loading documents.'
                />
              </>
            ) : null}

            {eei?.next_step ? (
              <>
                <Divider />
                <DetailRow label='Next Step' value={eei?.next_step} />
              </>
            ) : null}

            {notes.length > 0 ? (
              <>
                <Divider />
                <DetailRow label='Notes' value={notes.join(' ')} />
              </>
            ) : null}
          </>
        )}
      </div>
    </>
  );
};

export default ProductProfileResults;
